/**
 * backgroundLast — draw a node background AFTER the depth-writing opaques, depth-tested at the far plane (the shader
 * sweep, 2026-10-06). An engine optimisation, output-identical by construction; not an upstream backport.
 *
 * WHY. three r185.1's `Background.update` puts the background mesh (a sphere whose vertex stage pins it to the far plane)
 * at the FRONT of the opaque list with `depthTest: false`: the sky's fragment shader runs on EVERY pixel of the frame and
 * the ground then paints over most of them. Atlas's sky is not cheap — the 4-LUT atmosphere + the sun disc + the cirrus
 * sheet + the night layer on WebGPU, and on WebGL2 (every Quest / VR frame) an inline Hillaire march of ~270 density
 * samples per pixel. Drawn after the opaques with a LessEqual test against the cleared depth, early-z rejects every pixel
 * geometry already covers (the classic skybox order — Khronos "Skybox"; Unity draws its skybox after opaques for early-z).
 *
 * WHY IT IS THE SAME PICTURE. The background writes no depth and is drawn where nothing drew (its depth equals the clear
 * value there, so LessEqual passes exactly on the sky pixels): every covered pixel ends with the geometry's colour and
 * MRT outputs either way. The background is moved only past opaques that WRITE depth and test it — anything that does not
 * (a depthWrite:false or depthTest:false opaque that would have painted over the sky) keeps its place after the
 * background, as in three. `skyMask()` is unaffected (sky pixels keep the clear depth).
 *
 * SCOPE. A node background only (a colour background is a clear). Stands down for a renderer with a reversed depth
 * buffer (the background is pinned to NDC z = 1, the NEAR plane there), for a pass that does not clear its depth to 1
 * (a second scene composited over a first), and for a pass with no depth attachment. The depth-tested twin is a separate
 * material (its own pipeline), so a pass that keeps three's order keeps three's material. Kill switch:
 * `localStorage enginex:background-last = 'off'`, or `installBackgroundLast(renderer, { enabled: false })`.
 */

interface RenderItem { object?: { name?: string; isMesh?: boolean }; material?: MaterialLike | null }
interface MaterialLike {
  name?: string; depthTest?: boolean; depthWrite?: boolean; version?: number; needsUpdate?: boolean;
  clone?: () => MaterialLike;
}
interface RenderListLike { opaque?: RenderItem[] }
interface RenderContextLike { clearDepth?: boolean; depthClearValue?: number; depth?: boolean }
interface BackgroundLike { update?: (scene: unknown, list: RenderListLike, ctx: RenderContextLike) => unknown }
interface RendererLike {
  _background?: BackgroundLike; reversedDepthBuffer?: boolean; __enginexBackgroundLast?: boolean;
  clear?: (color?: boolean, depth?: boolean, stencil?: boolean) => unknown;
  render?: (...a: unknown[]) => unknown;
  getRenderTarget?: () => unknown;
  getClearDepth?: () => number;
}

export interface BackgroundLastStats { installed: boolean; reason: string; moved: number; keptThree: number }
const stats: BackgroundLastStats = { installed: false, reason: 'not installed', moved: 0, keptThree: 0 };
export function backgroundLastStats(): BackgroundLastStats { return { ...stats }; }
let live = true;
/** A runtime switch for an installed patch (A/B timing in one page; the localStorage key is the boot switch). */
export function setBackgroundLastEnabled(on: boolean): void { live = on; }

function storageOff(): boolean {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('enginex:background-last') === 'off'; } catch { return false; }
}

const BG_MESH = 'Background.mesh';
const BG_MAT = 'Background.material';

/** The depth-tested twin of three's background material, kept in step with it (a background-node change re-builds it). */
function lastMaterialFor(src: MaterialLike & { __enginexLast?: MaterialLike; __enginexLastVersion?: number }): MaterialLike | null {
  let twin = src.__enginexLast;
  if (!twin) {
    if (typeof src.clone !== 'function') return null;
    twin = src.clone();
    twin.name = 'Background.material.last';
    twin.depthTest = true; // LessEqual (the material default) against the cleared far plane
    twin.depthWrite = false;
    src.__enginexLast = twin;
    src.__enginexLastVersion = src.version;
  } else if (src.__enginexLastVersion !== src.version) {
    // three re-points the background's colour node in place and bumps the source material; the twin shares that node.
    twin.needsUpdate = true;
    src.__enginexLastVersion = src.version;
  }
  return twin;
}

/**
 * Where the background goes: after the leading run of opaques that write AND test depth (only those are guaranteed to
 * hide it), before the first that does not — so such an item still paints over the background as it did in three.
 */
export function backgroundInsertIndex(opaque: ReadonlyArray<RenderItem>, from = 0): number {
  for (let i = from; i < opaque.length; i++) {
    const m = opaque[i]?.material;
    if (!m || m.depthWrite === false || m.depthTest === false) return i;
  }
  return opaque.length;
}

/** Reorder one pass's list (pure on the list; returns whether it moved the background). */
export function moveBackgroundLast(list: RenderListLike, ctx: RenderContextLike, reversed: boolean, depthFresh = false): boolean {
  const opaque = list.opaque;
  if (!opaque || opaque.length < 2) return false;
  const head = opaque[0];
  if (head?.object?.name !== BG_MESH || head.material?.name !== BG_MAT) return false;
  // The pass must start from a depth cleared to the far plane: three's own clear (autoClear — the post chain's scene
  // pass), or an explicit `renderer.clear(…, depth)` on this target with nothing drawn into it since (the engine's camera
  // stack, every XR frame) — `depthFresh`.
  const cleared = (ctx.clearDepth === true && ctx.depthClearValue === 1) || depthFresh;
  if (reversed || !cleared || ctx.depth === false) { stats.keptThree++; return false; }
  const at = backgroundInsertIndex(opaque, 1);
  if (at <= 1) { stats.keptThree++; return false; } // the first opaque does not write depth: three's order is the only safe one
  const twin = lastMaterialFor(head.material as MaterialLike);
  if (!twin) return false;
  opaque.shift();
  head.material = twin;
  opaque.splice(at - 1, 0, head);
  stats.moved++;
  return true;
}

/** Install on a renderer (idempotent). Returns whether it is active. */
export function installBackgroundLast(renderer: unknown, opts: { enabled?: boolean } = {}): boolean {
  const r = renderer as RendererLike | null;
  if (opts.enabled === false || storageOff()) { stats.installed = false; stats.reason = 'switched off'; return false; }
  const bg = r?._background;
  if (!r || !bg || typeof bg.update !== 'function') { stats.installed = false; stats.reason = 'no renderer background'; return false; }
  if (r.__enginexBackgroundLast) return true;
  r.__enginexBackgroundLast = true;
  // THE FRESH DEPTH: the engine clears explicitly (`autoClear = false`, then `renderer.clear(c, d, s)`), so three's pass
  // reports no clear. An explicit depth clear marks its target; the next render into that target starts from it; any
  // render consumes the mark (its depth now holds geometry). A nested render (a shadow map, a reflector, a pass node)
  // carries its own flag and restores the outer one.
  let clearedTarget: unknown = undefined; // undefined = no pending clear (null is the canvas)
  let fresh = false;
  if (typeof r.clear === 'function') {
    const origClear = r.clear.bind(r);
    r.clear = (color?: boolean, depth?: boolean, stencil?: boolean) => {
      const out = origClear(color, depth, stencil);
      // A depth clear writes getClearDepth(); only the standard far value 1 is the background's plane.
      if (depth !== false && (r.getClearDepth?.() ?? 1) === 1) clearedTarget = r.getRenderTarget?.() ?? null;
      return out;
    };
  }
  if (typeof r.render === 'function') {
    const origRender = r.render.bind(r);
    r.render = (...a: unknown[]) => {
      const target = r.getRenderTarget?.() ?? null;
      const outer = fresh;
      fresh = clearedTarget !== undefined && clearedTarget === target;
      clearedTarget = undefined;
      try { return origRender(...a); } finally { fresh = outer; }
    };
  }
  const orig = bg.update.bind(bg);
  bg.update = (scene, list, ctx) => {
    const out = orig(scene, list, ctx);
    // `fresh` belongs to ONE render: consumed here, so a second scene in the same render call never inherits it.
    const depthFresh = fresh; fresh = false;
    if (live) { try { moveBackgroundLast(list, ctx, r.reversedDepthBuffer === true, depthFresh); } catch { /* never break a frame over an optimisation */ } }
    return out;
  };
  stats.installed = true;
  stats.reason = 'node backgrounds drawn after the depth-writing opaques';
  return true;
}
